export const RICH_TEXT_MAX_BYTES = 100 * 1024;
const SUPPORTED_NODES = new Set([
    "doc",
    "paragraph",
    "heading",
    "text",
    "bulletList",
    "orderedList",
    "listItem",
    "blockquote",
    "horizontalRule",
    "hardBreak",
]);
const SUPPORTED_MARKS = new Set(["bold", "italic", "underline", "link"]);
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function utf8ByteLength(value) {
    let bytes = 0;
    for (const character of value) {
        const point = character.codePointAt(0) ?? 0;
        bytes += point <= 0x7f ? 1 : point <= 0x7ff ? 2 : point <= 0xffff ? 3 : 4;
    }
    return bytes;
}
function validateMark(value) {
    if (!isRecord(value) || typeof value.type !== "string")
        return false;
    if (!SUPPORTED_MARKS.has(value.type))
        return false;
    if (value.type === "link") {
        return isRecord(value.attrs) && typeof value.attrs.href === "string";
    }
    return value.attrs === undefined || isRecord(value.attrs);
}
function invalidNodeReason(value) {
    if (!isRecord(value) || typeof value.type !== "string") {
        return "The Rich Text document is malformed";
    }
    if (value.type === "table")
        return "Tables are not supported";
    if (!SUPPORTED_NODES.has(value.type)) {
        return `Rich Text element "${value.type}" is not supported`;
    }
    if (value.type === "text" && typeof value.text !== "string") {
        return "The Rich Text document is malformed";
    }
    if (value.type === "heading") {
        const level = isRecord(value.attrs) ? value.attrs.level : undefined;
        if (level !== 2 && level !== 3 && level !== 4) {
            return "Only level-two through level-four headings are supported";
        }
    }
    if (value.marks !== undefined) {
        if (!Array.isArray(value.marks) || !value.marks.every(validateMark)) {
            return "The Rich Text document contains unsupported formatting";
        }
    }
    if (value.content !== undefined) {
        if (!Array.isArray(value.content)) {
            return "The Rich Text document is malformed";
        }
        for (const child of value.content) {
            const reason = invalidNodeReason(child);
            if (reason)
                return reason;
        }
    }
    return null;
}
export function validateRichTextDocument(value) {
    if (!isRecord(value) || value.type !== "doc") {
        return { valid: false, reason: "The Rich Text document is malformed" };
    }
    const reason = invalidNodeReason(value);
    if (reason)
        return { valid: false, reason };
    if (utf8ByteLength(JSON.stringify(value)) > RICH_TEXT_MAX_BYTES) {
        return {
            valid: false,
            reason: "Rich Text content cannot exceed 100 KiB",
        };
    }
    return { valid: true, document: value };
}
export function isRichTextDocument(value) {
    return validateRichTextDocument(value).valid;
}
/**
 * Does this document carry anything a visitor could read?
 *
 * `EMPTY_RICH_TEXT_DOCUMENT` is a *valid* rich-text document — a paragraph with
 * no text — so structural validity cannot answer "is this blank?". The rule is
 * deliberately the simplest one that says what an Editor means: some
 * non-whitespace text, somewhere in the document. A document of nothing but a
 * separator and an empty paragraph is therefore blank, however well-formed it
 * is.
 *
 * It lives here, beside the structural rules, because it is a rich-text
 * question: every component that carries a rich-text body asks it the same way.
 */
export function richTextDocumentHasContent(document) {
    if (typeof document.text === "string" && document.text.trim() !== "") {
        return true;
    }
    return (document.content ?? []).some(richTextDocumentHasContent);
}
/** Count visible text, excluding document structure and formatting metadata. */
export function richTextDocumentTextLength(document) {
    return ((document.text?.length ?? 0) +
        (document.content ?? []).reduce((total, child) => total + richTextDocumentTextLength(child), 0));
}
/** Apply a document slot's optional copy limit after structural validation. */
export function validateDocumentSlotLength(slot, document) {
    if (slot.maxLength === undefined ||
        richTextDocumentTextLength(document) <= slot.maxLength) {
        return { valid: true };
    }
    return {
        valid: false,
        reason: `${slot.label} must be ${slot.maxLength} characters or fewer`,
    };
}
//# sourceMappingURL=rich-text-document.js.map